import type { ElementTheme } from '@examdojo/core/widget';
import { deepMerge } from '@examdojo/core/widget';
import type { GeometryElementDisplayConfig, GeometryDisplayConfig } from '../../geometrypad.model';

// =============================================================================
// TYPES
// =============================================================================

/** Maps geometry element type → its default style tree. */
export type GeometryShapeTheme = Record<string, GeometryElementDisplayConfig>;

/** Style types that can be resolved in geometry context. */
export type GeometryStyleType = 'point' | 'line' | 'polygon' | 'angle' | 'circle';

/** Context needed for geometry style resolution. */
export interface GeometryStyleContext {
  elementFactory: { getTheme: () => ElementTheme };
  shapeTheme: GeometryShapeTheme;
  geometryConfig: GeometryDisplayConfig;
}

// =============================================================================
// INTERNAL HELPERS
// =============================================================================

type AnyStyle = Record<string, unknown>;

/** Type-safe merge that works across interface boundaries. */
function mergeStyles(base: AnyStyle, overrides: unknown): AnyStyle {
  if (!overrides || typeof overrides !== 'object') {
    return base;
  }
  return deepMerge(base, overrides as Partial<AnyStyle>);
}

// =============================================================================
// RESOLUTION
// =============================================================================

/**
 * Resolve a style for a geometry element's subpart through the 4-layer cascade.
 *
 * Cascading order (lowest → highest priority):
 * 1. ElementTheme (global JSXGraph factory defaults)
 * 2. GeometryShapeTheme[shapeType] root + subpart (per-shape defaults)
 * 3. geometryConfig flag interpretation (neutralize colors, zero fill, etc.)
 * 4. Per-element instance overrides (displayConfig.elements[id])
 */
export function resolveGeometryStyle<T extends GeometryStyleType>(
  styleType: T,
  shapeType: string,
  context: GeometryStyleContext,
  elementDisplay: GeometryElementDisplayConfig | undefined,
  subpartName?: string,
): NonNullable<GeometryElementDisplayConfig[T]> {
  const { elementFactory, shapeTheme, geometryConfig } = context;

  // Layer 1: ElementTheme global defaults
  const theme = elementFactory.getTheme();
  let style: AnyStyle = styleType in theme ? { ...(theme[styleType as keyof ElementTheme] as AnyStyle) } : {};

  // Layer 2: Shape theme defaults (root-level + subpart)
  const shapeNode = shapeTheme[shapeType];
  if (shapeNode) {
    const rootStyle = shapeNode[styleType];
    if (rootStyle) {
      style = mergeStyles(style, rootStyle);
    }
    if (subpartName) {
      const subpartStyle = shapeNode.subparts?.[subpartName]?.[styleType];
      if (subpartStyle) {
        style = mergeStyles(style, subpartStyle);
      }
    }
  }

  // Layer 3: geometryConfig semantic flag overrides
  style = applyGeometryConfigOverrides(styleType, style, geometryConfig);

  // Layer 4: Per-element instance overrides (root-level + subpart)
  if (elementDisplay) {
    const rootOverride = elementDisplay[styleType];
    if (rootOverride) {
      style = mergeStyles(style, rootOverride);
    }
    if (subpartName) {
      const subpartOverride = elementDisplay.subparts?.[subpartName]?.[styleType];
      if (subpartOverride) {
        style = mergeStyles(style, subpartOverride);
      }
    }
  }

  return style as NonNullable<GeometryElementDisplayConfig[T]>;
}

// =============================================================================
// GEOMETRY CONFIG FLAG INTERPRETATION (LAYER 3)
// =============================================================================

/**
 * Apply geometryConfig semantic flags as style overrides.
 *
 * - `shape.coloring === false` → neutralize line/polygon colors
 * - `shape.fill === false` → set polygon fillOpacity to 0
 * - `angle.coloring === false` → neutralize angle colors
 * - `angle.fill === false` → set angle fillOpacity to 0
 */
function applyGeometryConfigOverrides(
  styleType: GeometryStyleType,
  style: AnyStyle,
  geometryConfig: GeometryDisplayConfig,
): AnyStyle {
  const shapeColoring = geometryConfig.shape?.coloring === true;
  const shapeFill = geometryConfig.shape?.fill !== false;
  const angleColoring = geometryConfig.angle?.coloring !== false;
  const angleFill = geometryConfig.angle?.fill !== false;

  if (styleType === 'line') {
    if (!shapeColoring) {
      return mergeStyles(style, { color: 'neutral' });
    }
  }

  if (styleType === 'polygon') {
    const overrides: AnyStyle = {};
    if (!shapeColoring) {
      overrides['fillColor'] = 'neutral';
    }
    if (!shapeFill) {
      overrides['fillOpacity'] = 0;
      overrides['fillColor'] = undefined;
    }
    if (Object.keys(overrides).length > 0) {
      return mergeStyles(style, overrides);
    }
  }

  if (styleType === 'angle') {
    const overrides: AnyStyle = {};
    if (!angleColoring) {
      overrides['fillColor'] = 'neutral';
      overrides['strokeColor'] = 'neutral';
    }
    if (!angleFill) {
      overrides['fillOpacity'] = 0;
    }
    if (Object.keys(overrides).length > 0) {
      return mergeStyles(style, overrides);
    }
  }

  return style;
}

/**
 * Resolve the label color for a geometry element's subpart.
 *
 * Label coloring follows `geometryConfig.label.coloring`. When disabled,
 * returns 'neutral'. Otherwise extracts the color from the shape theme
 * subpart defaults, with per-element overrides applied on top.
 */
export function resolveGeometryLabelColor(
  shapeType: string,
  context: GeometryStyleContext,
  elementDisplay: GeometryElementDisplayConfig | undefined,
  subpartName: string,
  styleType: 'line' | 'angle' = 'line',
): string {
  const labelColoring = context.geometryConfig.label?.coloring !== false;

  if (!labelColoring) {
    return 'neutral';
  }

  const colorKey = styleType === 'line' ? 'color' : 'fillColor';
  let color = 'neutral';

  // Get color from the shape theme subpart
  const shapeNode = context.shapeTheme[shapeType];
  if (shapeNode?.subparts?.[subpartName]?.[styleType]) {
    const subpartStyle = shapeNode.subparts[subpartName][styleType] as AnyStyle;
    if (subpartStyle[colorKey]) {
      color = subpartStyle[colorKey] as string;
    }
  }

  // Per-element override
  if (elementDisplay?.subparts?.[subpartName]?.[styleType]) {
    const subpartStyle = elementDisplay.subparts[subpartName][styleType] as AnyStyle;
    if (subpartStyle[colorKey]) {
      color = subpartStyle[colorKey] as string;
    }
  }

  return color;
}
